import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, FileText, Settings, Bell, LogOut, 
  ShieldCheck, Clock, Search, Plus, Trash2, Edit, Upload, 
  Image as ImageIcon, Users, MapPin, RefreshCw, Eye, Briefcase, ChevronRight
} from 'lucide-react';
import { api, getMediaUrl, LoadingSpinner, Badge, Modal } from '../utils';

// --- Sub-Components ---
const AdminOverview = ({ stats }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl"><p className="text-slate-400 text-sm font-medium">Total Revenue</p><p className="text-3xl font-black mt-1">?{stats?.revenue || 0}</p></div>
      <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl"><p className="text-slate-400 text-sm font-medium">Active Users</p><p className="text-3xl font-black mt-1">{stats?.users || 0}</p></div>
      <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl"><p className="text-slate-400 text-sm font-medium">Total Orders</p><p className="text-3xl font-black mt-1">{stats?.orders || 0}</p></div>
      <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl"><p className="text-slate-400 text-sm font-medium">Documents</p><p className="text-3xl font-black mt-1">{stats?.docs || 0}</p></div>
    </div>
  </div>
);

const AdminOrders = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const fetchOrders = () => api.get('/admin/orders', token).then(setOrders).catch(console.error);
  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (orderId, status) => {
    try { await api.put(`/admin/order-status?order_id=${orderId}&status=${status}`, {}, token); fetchOrders(); } 
    catch (err) { alert('Failed'); }
  };

  return (
    <div className="bg-slate-900 rounded-3xl overflow-hidden border border-slate-800">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-950 text-slate-500 text-xs uppercase">
            <tr><th className="px-6 py-4">ID</th><th className="px-6 py-4">Customer</th><th className="px-6 py-4">Service</th><th className="px-6 py-4">Status</th><th className="px-6 py-4 text-right">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {orders.map(o => (
              <tr key={o.order_id} className="hover:bg-slate-800/50">
                <td className="px-6 py-4 font-mono text-xs text-slate-400">#{o.order_id}</td>
                <td className="px-6 py-4"><p className="font-bold text-slate-200">{o.customer_name}</p></td>
                <td className="px-6 py-4 text-slate-300 font-medium">{o.service_name}</td>
                <td className="px-6 py-4"><Badge status={o.status}>{o.status}</Badge></td>
                <td className="px-6 py-4 text-right">
                  <select value={o.status} onChange={(e) => updateStatus(o.order_id, e.target.value)} className="bg-slate-950 border border-slate-700 text-xs rounded-lg px-2 py-1 outline-none">
                    <option value="PENDING">PENDING</option><option value="IN_PROCESS">IN_PROCESS</option><option value="READY">READY</option><option value="DELIVERED">DELIVERED</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminCustomers = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const fetchCustomers = () => api.get('/admin/customers', token).then(setCustomers).catch(console.error);
  useEffect(() => { fetchCustomers(); }, []);

  return (
    <div className="bg-slate-900 rounded-3xl overflow-hidden border border-slate-800">
      <table className="w-full text-left">
        <thead className="bg-slate-950 text-slate-500 text-xs uppercase"><tr><th className="px-6 py-4">Name</th><th className="px-6 py-4">Contact</th></tr></thead>
        <tbody className="divide-y divide-slate-800">
          {customers.map(c => (
            <tr key={c.id} className="hover:bg-slate-800/50">
              <td className="px-6 py-4 text-slate-200 font-bold">{c.name}</td><td className="px-6 py-4 text-slate-300 text-sm">{c.email} / {c.mobile}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminBranches = ({ token }) => {
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get('/admin/branches', token).then(setBranches).catch(console.error); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {branches.map(b => (
        <div key={b.id} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl"><h4 className="text-lg font-bold text-white">{b.name}</h4><p className="text-slate-500 text-sm mt-1">{b.city}</p></div>
      ))}
    </div>
  );
};

const AdminServices = ({ token }) => {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get('/admin/services', token).then(setServices).catch(console.error); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {services.map(s => (
        <div key={s.id} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl"><h4 className="font-bold text-lg text-white mb-1">{s.name}</h4><p className="text-slate-500 text-sm">{s.description}</p></div>
      ))}
    </div>
  );
};

// --- Main Export ---
export default function AdminDashboard({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard', token).then(setStats).catch(console.error).finally(() => setLoading(false));
  }, []);

  const AdminSidebarItem = ({ id, icon: Icon, label }) => (
    <button onClick={() => setActiveTab(id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium ${activeTab === id ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}>
      <Icon size={20} /> <span className="hidden lg:inline">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <aside className="w-20 lg:w-64 bg-slate-900 border-r border-slate-800 flex flex-col p-4 fixed h-full z-30">
        <div className="flex items-center gap-2 mb-10 px-2"><div className="bg-indigo-600 p-2 rounded-lg"><ShieldCheck className="text-white" size={20} /></div><span className="text-xl font-bold text-white hidden lg:inline">Admin</span></div>
        <nav className="flex-1 space-y-1">
          <AdminSidebarItem id="overview" icon={LayoutDashboard} label="Overview" />
          <AdminSidebarItem id="orders" icon={Briefcase} label="Orders" />
          <AdminSidebarItem id="customers" icon={Users} label="Customers" />
          <AdminSidebarItem id="branches" icon={MapPin} label="Branches" />
          <AdminSidebarItem id="services" icon={Settings} label="Services" />
        </nav>
        <div className="pt-4 border-t border-slate-800">
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 text-rose-400 rounded-xl font-medium"><LogOut size={20} /> <span className="hidden lg:inline">Sign Out</span></button>
        </div>
      </aside>

      <main className="flex-1 ml-20 lg:ml-64 p-4 lg:p-8 bg-slate-950 text-white min-h-screen">
        <header className="flex justify-between items-center mb-10"><div><h1 className="text-2xl font-bold capitalize">{activeTab}</h1></div></header>
        {loading ? <LoadingSpinner /> : (
          <>
            {activeTab === 'overview' && <AdminOverview stats={stats} />}
            {activeTab === 'orders' && <AdminOrders token={token} />}
            {activeTab === 'customers' && <AdminCustomers token={token} />}
            {activeTab === 'branches' && <AdminBranches token={token} />}
            {activeTab === 'services' && <AdminServices token={token} />}
          </>
        )}
      </main>
    </div>
  );
}